/**
 * Faye travel insurance pricing, for quoting at Defera checkout.
 *
 * Plain JS on purpose, like stay-cancel-rules.js: checkout prices in the
 * browser and the checkout functions re-price on the server from this same
 * file, so the two can never disagree about what Faye charges.
 *
 * Faye declined to give Defera a quoting API, so this reproduces their prices
 * from 43 quotes (115 tier prices) Muse ran in the Faye Advisor Portal on
 * 2026-10-02/03 — the quote matrix is the test set in faye-pricing.test.js,
 * and every quote it reproduces to the cent is asserted there. Muse's later
 * rounds (21 more quotes, 2026-10-03) pinned the youth band at 18–34 at any
 * trip cost, three-adult parties, GA/NJ/NC (priced like CA), and how a child
 * traveling with an adult is priced.
 *
 * Faye prices each traveler and rounds each to the cent before adding them
 * up: three 40-year-olds on a $2,000 trip are 3 × $75.55 = $226.65 on First,
 * where rounding the party once gives $226.64.
 *
 *   premium = Σ per traveler, each rounded to the cent:
 *               base + age fee + (slope + age rate) × their share of the trip cost ÷ $1,000
 *           + nightly rate × nights beyond 13, per traveler
 *
 *   A child (0–17) traveling with an adult is priced on their own scale:
 *   child base + child rate × their share ÷ $1,000 — about $11.90 + $12.99/k
 *   on First, $15.52 + $13.20/k on Forward. The family of four (40, 38, 10, 6)
 *   at $2,000: 2 × $70.18 + 2 × $18.39 = $177.14.
 *
 * - Nights are return date minus departure date (a Dec 15 → Dec 29 trip is
 *   14 nights: one chargeable night).
 * - A senior pays a flat fee plus a rate on their SHARE of the trip cost, by
 *   band (60–65, 66–70, 71+): a 70-year-old on a $5,000 trip is $73.26 +
 *   $24.28 + ($32.90 + $22.80) × 5 = $376.04 on Forward. (The "minimum per
 *   senior" an earlier fit needed was this flat fee.)
 * - Destination has no effect. Faye insures from $1,000 of trip cost (or $0:
 *   no cancellation cover) up to $100,000 per traveler and $300,000 per
 *   trip; cheaper trips are priced at $1,000.
 *
 * Plan names, taglines, coverage and timing rules come from Faye's own plan
 * guides (2026). Customer-facing text is taken only from the consumer
 * one-pager and the trip cancellation/interruption sheets; the partner
 * one-pager and objection-handling sheet are marked "For Travel Advisor Use
 * Only - Do Not Distribute". The guides corrected the portal notes: Forward's 150% is trip
 * INTERRUPTION (cancellation is up to 100% on every plan), and Flagship's
 * $1M is medical EVACUATION (its medical cover is $250K).
 *
 * `exact` says whether the inputs sit inside what the portal quotes pinned
 * down; `notes` say why not. Anything not exact must be checked in the portal
 * before purchase, and the customer is charged exactly what Faye charges —
 * insurance can't be marked up.
 */

/** Age bands (completed years). `tested` = the band edge was quoted.
 *  - Youth (18–34): a flat discount per $1,000 of the traveler's share at any
 *    trip cost (28 saves $5 at $1,000 and $25 at $5,000 on First). 34 is the
 *    last youth age; 35 pays full adult price.
 *  - Children (0–17): one band. With an adult they're priced on the family
 *    child scale (see FAYE_TIERS.*.familyChild); traveling alone, at the
 *    `child` age rate, which was quoted only on a $2,000 trip. */
function ageBand(age) {
  if (!Number.isFinite(age) || age < 0) return { key: 'standard', tested: false, note: 'age unknown' };
  if (age >= 71) return { key: 'senior71', tested: age <= 75, note: 'ages over 75 not quoted' };
  if (age >= 66) return { key: 'senior66', tested: true };
  if (age >= 60) return { key: 'senior60', tested: true };
  if (age >= 35) return { key: 'standard', tested: true };
  if (age >= 18) return { key: 'youth', tested: true };
  return { key: 'child', tested: true };
}

/** Covered reasons Faye First leaves out of trip cancellation AND trip
 *  interruption (Forward and Flagship cover all three). For a flights
 *  business these are the common ones, which is the case for Forward. */
const FIRST_EXCLUDED_REASONS = Object.freeze([
  'Bad weather delays or closures',
  'Mechanical breakdown of the airline (or cruise line)',
  'An airport shutdown from fire or a power outage',
]);

/** Per-tier constants, dollars. Age rates are per $1,000 of a traveler's
 *  share of the trip cost; minimums are per traveler where a quote showed one.
 *  Coverage text is Faye's, from its consumer plan summary; `highlights`
 *  (the card's bullet points) and `compare` (the comparison table) are
 *  Defera's short forms of it — a test keeps every figure in them agreeing
 *  with `coverage`. Faye approves all of this copy before launch. */
export const FAYE_TIERS = {
  first: {
    key: 'first',
    label: 'Faye First',
    tagline: 'Putting travelers first with holistic coverage that covers the essentials & keeps trips on track.',
    base: 54.08,
    slope: 32.2,
    perNight: 1.3,
    // `child`: a child traveling alone ($68.29 at $2,000, the one cost quoted).
    ageRates: { child: -25.095, youth: -5.0, standard: 0, senior60: 13.0, senior66: 22.3, senior71: 32.3 },
    /** A senior's flat fee, on top of the band's rate on their share. */
    ageFees: { senior60: 5.33, senior66: 6.82, senior71: 7.56 },
    /** A child traveling with an adult: base + rate × their share ÷ $1,000,
     *  and their own nightly rate past night 13 (one quote: provisional).
     *  Muse's "$12.99/k" is the rate to the cent; the three family quotes pin
     *  it in [12.985, 12.99) — at 12.99 a $500 share is $18.395, which Faye
     *  charges as $18.39 (#56). */
    familyChild: { base: 11.9, rate: 12.9875, perNight: 0.36 },
    /** Residence states quoted (2026-10-03: GA, NJ and NC price like CA; TX too). */
    quotedStates: ['CA', 'TX', 'GA', 'NJ', 'NC'],
    coverage: {
      cancellation: 'Up to 100% of non-refundable trip costs',
      interruption: 'Up to 100% of non-refundable trip costs',
      delay: 'Up to $2,500 ($250/day) after a 6-hour delay',
      missedConnection: 'Up to $500 after a 3-hour delay',
      inconvenience: '$200 per inconvenience, up to $600',
      medical: 'Up to $50,000 (excess), incl. $750 dental',
      evacuation: 'Up to $250,000',
      bedsideCompanion: null,
      baggage: 'Up to $2,000 per person ($150 per item)',
      baggageDelay: 'Up to $300 per person after 12 hours',
    },
    excludedReasons: FIRST_EXCLUDED_REASONS,
    cancelForWorkReasons: false,
    highlights: [
      'Trip cancellation up to 100%',
      'Flight delays up to $2,500',
      'Emergency medical up to $50K',
      'Lost or damaged bags up to $2K',
    ],
    compare: { cancellation: '100%', interruption: '100%', weatherAndAirline: false, delay: '$2,500', missedConnection: '$500', medical: '$50K', evacuation: '$250K', baggage: '$2K' },
    /** Faye First is a domestic plan; trips abroad get Faye First
     *  International, with lower delay, connection and bag limits and higher,
     *  primary medical (Faye's plan comparison sheet, 2026). Forward and
     *  Flagship are one plan for both. The portal priced First the same
     *  whatever the destination. Faye's sheets don't list First
     *  International's covered reasons separately, so it keeps First's
     *  exclusions until Faye says otherwise. */
    international: {
      label: 'Faye First International',
      coverage: {
        cancellation: 'Up to 100% of non-refundable trip costs',
        interruption: 'Up to 100% of non-refundable trip costs',
        delay: 'Up to $1,200 ($200/day) after a 6-hour delay',
        missedConnection: 'Up to $300 after a 3-hour delay',
        inconvenience: '$200 per inconvenience, up to $600',
        medical: 'Up to $100,000 (primary), incl. $750 dental',
        evacuation: 'Up to $250,000',
        bedsideCompanion: null,
        baggage: 'Up to $1,000 per person ($200 per item)',
        baggageDelay: 'Up to $300 per person after 12 hours',
      },
      highlights: [
        'Trip cancellation up to 100%',
        'Flight delays up to $1,200',
        'Emergency medical up to $100K',
        'Lost or damaged bags up to $1K',
      ],
      compare: { cancellation: '100%', interruption: '100%', weatherAndAirline: false, delay: '$1,200', missedConnection: '$300', medical: '$100K', evacuation: '$250K', baggage: '$1K' },
    },
  },
  forward: {
    key: 'forward',
    label: 'Faye Forward',
    tagline: 'Pushing great trips forward with whole-trip coverage, higher limits & broader protection.',
    base: 73.26,
    slope: 32.9,
    perNight: 4.57,
    ageRates: { child: -30.32, youth: -5.2, standard: 0, senior60: 13.2, senior66: 22.8, senior71: 33.0 },
    // 71+: Muse wrote $25.43; four quotes ($2k–$5k) all fit $25.41 exactly.
    ageFees: { senior60: 18.98, senior66: 24.28, senior71: 25.41 },
    familyChild: { base: 15.52, rate: 13.2, perNight: 1.27 },
    quotedStates: ['CA', 'TX', 'GA', 'NJ', 'NC'],
    coverage: {
      cancellation: 'Up to 100% of non-refundable trip costs',
      interruption: 'Up to 150% of non-refundable trip costs',
      delay: 'Up to $3,500 ($250/day) after a 6-hour delay',
      missedConnection: 'Up to $500 after a 3-hour delay',
      inconvenience: '$200 per inconvenience, up to $600',
      medical: 'Up to $250,000 (primary), incl. $750 dental',
      evacuation: 'Up to $500,000',
      bedsideCompanion: 'Up to $1,000',
      baggage: 'Up to $2,000 per person ($200 per item)',
      baggageDelay: 'Up to $300 per person after 12 hours',
    },
    excludedReasons: [],
    cancelForWorkReasons: true,
    highlights: [
      'Covers weather & airline breakdowns',
      'Trip interruption up to 150%',
      'Flight delays up to $3,500',
      'Emergency medical up to $250K',
    ],
    compare: { cancellation: '100%', interruption: '150%', weatherAndAirline: true, delay: '$3,500', missedConnection: '$500', medical: '$250K', evacuation: '$500K', baggage: '$2K' },
  },
  flagship: {
    key: 'flagship',
    label: 'Faye Flagship',
    tagline: "Putting our flagship whole-trip coverage & care in travelers' hands with premium protection.",
    base: 105.04,
    slope: 30.5,
    perNight: 6.47,
    // Flagship children were never quoted: an estimate, flagged. Its 60–65
    // and 71+ seniors were quoted only at a $2,000 share, so the whole charge
    // sits in the rate there; 66–70 (three quotes) splits into fee + rate.
    ageRates: { child: -30.0, youth: -5.195, standard: 0, senior60: 32.215, senior66: 34.6, senior71: 57.705 },
    ageFees: { senior66: 30.21 },
    seniorShareQuoted: { senior60: 2000, senior71: 2000 },
    quotedStates: ['CA'],
    /** Flagship alone moved with trip cost and state in the quotes. */
    tenThousandStep: 10.48,
    stateAdjustment: { TX: 8.83 },
    coverage: {
      cancellation: 'Up to 100% of non-refundable trip costs',
      interruption: 'Up to 150% of non-refundable trip costs',
      delay: 'Up to $4,500 ($300/day) after a 6-hour delay',
      missedConnection: 'Up to $1,000 per person after a 3-hour delay',
      inconvenience: '$250 per inconvenience, up to $1,000',
      medical: 'Up to $250,000 (primary), incl. $750 dental; up to $500,000 with the add-on upgrade',
      evacuation: 'Up to $1,000,000',
      bedsideCompanion: 'Up to $1,000',
      baggage: 'Up to $2,000 per person ($250 per item)',
      baggageDelay: 'Up to $300 per person after 12 hours',
    },
    excludedReasons: [],
    cancelForWorkReasons: true,
    highlights: [
      'Covers weather & airline breakdowns',
      'Missed connections up to $1K',
      'Flight delays up to $4,500',
      'Medical evacuation up to $1M',
    ],
    compare: { cancellation: '100%', interruption: '150%', weatherAndAirline: true, delay: '$4,500', missedConnection: '$1K', medical: '$250K', evacuation: '$1M', baggage: '$2K' },
  },
};

/**
 * The plan a customer is offered for a tier. Faye First becomes Faye First
 * International on trips abroad (same price, different coverage); Forward and
 * Flagship are one plan everywhere.
 */
export function fayePlan(tier, { international = false } = {}) {
  const t = FAYE_TIERS[tier];
  if (!t) return null;
  const { international: abroad, ...plan } = t;
  return international && abroad ? { ...plan, ...abroad } : plan;
}

/**
 * Defera offers Faye only when the trip's base fare is at least this many
 * dollars (founder decision, 2026-10-02). Faye prices every trip under
 * $1,000 as if it cost $1,000, so on cheap trips the premium is a large
 * share of the fare.
 */
export const FAYE_MIN_BASE_FARE = 800;

/**
 * Faye's timing and limit rules (plan guides + objection-handling sheet).
 * Defera buys each policy by hand after checkout, so ops has to land the
 * purchase inside these windows, not just the customer.
 */
export const FAYE_RULES = Object.freeze({
  maxTripCostPerTraveler: 100000,
  maxTripCostPerTrip: 300000,
  /** Cancel For Any Reason, Cancel For Work Reasons and the pre-existing
   *  medical condition coverage must be bought within 14 days of the
   *  customer's first trip payment. */
  timeSensitiveWindowDays: 14,
  /** CFAR pays up to 75%, and only when the trip is cancelled 48+ hours out. */
  cfarReimbursement: 0.75,
  cfarMinHoursBeforeDeparture: 48,
  /** Faye's covered-reason lists assume the policy is bought 3+ days before
   *  departure. */
  fullCoverageMinDaysBeforeDeparture: 3,
  /** The customer can review and cancel a new plan within 14 days. */
  freeLookDays: 14,
});

/**
 * Faye's disclosure for travel retailers, verbatim from its consumer plan
 * summary (2026). The retailer paragraph is what checkout shows inline; the
 * product summary is the full text. Legal confirms placement before launch.
 * Note what it means for Defera's desk: a travel retailer may describe the
 * coverage and price, but must not answer technical questions about terms,
 * benefits, exclusions or conditions — those go to Faye.
 */
export const FAYE_RETAILER_DISCLOSURE =
  'In most states, your travel retailer is not a licensed insurance producer/agent, and is not qualified or authorized to '
  + 'answer technical questions about the terms, benefits, exclusions and conditions of the insurance offered or to evaluate '
  + 'the adequacy of your existing insurance coverage. Your travel retailer may be compensated for the purchase of a plan and '
  + 'may provide general information about the plans offered, including a description of the coverage and price. The '
  + 'purchase of travel insurance is not required in order to purchase any other product or service from your travel '
  + 'retailer. CA DOI toll free number is 800-927-4357. The cost of your plan is for the entire plan, which consists of both '
  + 'insurance and non-insurance components.';

/**
 * The trip-coverage disclosure Faye's travel-retailer training (signed with
 * the partnership agreement, 2026-02-07) requires in marketing and
 * fulfillment materials — "something similar to" this text, which corrects
 * the training slide's "may with to compare". Show it with the retailer
 * disclosure before purchase and repeat it in the confirmation email.
 */
export const FAYE_TRIP_COVERAGE_DISCLOSURE =
  'This plan provides insurance coverage that only applies during the covered trip. You may have coverage from other '
  + 'sources that provides you with similar benefits but may be subject to different restrictions depending upon your other '
  + 'coverages. You may wish to compare the terms of this policy with your existing life, health, home, and automobile '
  + 'insurance policies. If you have any questions about your current coverage, call your insurer or insurance agent or broker.';

export const FAYE_PRODUCT_SUMMARY_DISCLOSURE =
  'This Product Summary contains highlights of the plans developed by Faye; Faye is the brand name for customizable travel '
  + 'protection plans offered by Zenner, Inc. and its subsidiaries including Zenner Insurance Services LLC., which include '
  + 'travel insurance coverages underwritten by United States Fire Insurance Company (USF), Principal Office located in '
  + 'Morristown, New Jersey, under form series T7000 et al T210 et al and TP-401 et al, and non-insurance Travel Assistance '
  + 'Services provided by Zenner, Inc., and Falck Global Assistance LLC. The terms of insurance coverages in the plans may '
  + 'vary by jurisdiction and not all insurance coverages are available in all jurisdictions. Insurance coverages in these '
  + 'plans are subject to terms, limitations and exclusions including an exclusion for pre-existing medical conditions. '
  + `${FAYE_RETAILER_DISCLOSURE} Individuals looking to obtain additional information regarding the features and pricing of `
  + 'each travel plan component, please contact: Faye; 200 Continental Dr Suite 401; Newark, DE 19713, USA. For pre-trip or '
  + 'in-trip questions support@withfaye.com; Toll-free from the U.S +1-833-240-7056 or from outside of the U.S '
  + '+1-804-482-2122; California license #6006067. While Faye markets the travel insurance in these plans on behalf of USF, '
  + 'non-insurance components of the plans were added to the plans by Zenner Insurance Services LLC., or its parent company '
  + 'Zenner Inc., and Zenner Insurance Services LLC., or its parent company Zenner Inc., does not receive compensation from '
  + 'USF for providing the non-insurance components of the plans.';

export const FAYE_MIN_TRIP_COST = 1000;
/** Cancel For Any Reason: 2.5% of the insured trip cost on every tier
 *  ($50 at $2k, $125 at $5k, $250 at $10k — exact). */
export const FAYE_CFAR_RATE = 0.025;

const DAY_MS = 86400000;
const toUtcDay = (v) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v ?? ''));
  return m ? Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
};

/** Nights between two YYYY-MM-DD(…) dates, or null when either is missing. */
export function fayeTripNights(departDate, returnDate) {
  const a = toUtcDay(departDate);
  const b = toUtcDay(returnDate);
  if (a == null || b == null || b < a) return null;
  return Math.round((b - a) / DAY_MS);
}

const cents = (x) => Math.round(x * 100);

/** IANA zones of the 50 states and DC. US territories (Puerto Rico, the US
 *  Virgin Islands, Guam…) are left out: Faye's sheets say "Domestic trips"
 *  without defining them, so territory trips get Faye First International
 *  until Faye says otherwise. */
const US_STATE_TIMEZONES = new Set([
  'America/New_York', 'America/Detroit', 'America/Kentucky/Louisville', 'America/Kentucky/Monticello',
  'America/Indiana/Indianapolis', 'America/Indiana/Vincennes', 'America/Indiana/Winamac', 'America/Indiana/Marengo',
  'America/Indiana/Petersburg', 'America/Indiana/Vevay', 'America/Indiana/Tell_City', 'America/Indiana/Knox',
  'America/Chicago', 'America/Menominee', 'America/North_Dakota/Center', 'America/North_Dakota/New_Salem',
  'America/North_Dakota/Beulah', 'America/Denver', 'America/Boise', 'America/Phoenix', 'America/Los_Angeles',
  'America/Anchorage', 'America/Juneau', 'America/Sitka', 'America/Metlakatla', 'America/Yakutat', 'America/Nome',
  'America/Adak', 'Pacific/Honolulu',
]);

/**
 * 'domestic' when every airport on the trip is in the 50 states or DC,
 * 'international' when any is abroad, null when that can't be told (an
 * airport the table doesn't know, and none known to be abroad).
 *
 * `timezoneForAirport` is the caller's airport table — server/airport-
 * timezones.js or its Deno twin — so this file stays runtime-free.
 */
export function fayeTripRegion(airportCodes, timezoneForAirport) {
  const codes = (airportCodes || []).map((c) => String(c || '').trim().toUpperCase()).filter(Boolean);
  if (!codes.length || typeof timezoneForAirport !== 'function') return null;
  let unknown = false;
  for (const code of codes) {
    const zone = timezoneForAirport(code);
    if (!zone) unknown = true;
    else if (!US_STATE_TIMEZONES.has(zone)) return 'international';
  }
  return unknown ? null : 'domestic';
}

/**
 * Whether checkout offers Faye on a trip, and if not, why. The reasons are
 * for logs and ops; a customer on an ineligible trip just sees no offer.
 *
 * @param {object} p
 * @param {number}   p.baseFare           the trip's base fare, dollars (all travelers)
 * @param {number[]} p.ages               every traveler's age, lap infants included
 * @param {number}   p.tripCost           dollars Faye would insure (the trip total)
 * @param {string}   p.departDate         YYYY-MM-DD
 * @param {string}   [p.returnDate]       YYYY-MM-DD; one-way trips have none
 * @param {'domestic'|'international'|null} p.region  from fayeTripRegion
 * @param {number}   p.remainingPayments  installments after the deposit
 * @param {string}   [p.today]            YYYY-MM-DD (default: now)
 */
export function fayeOfferEligibility({ baseFare, ages, tripCost, departDate, returnDate, region, remainingPayments, today = null }) {
  const no = (reason, detail) => ({ eligible: false, reason, detail });
  if (!(Number(baseFare) >= FAYE_MIN_BASE_FARE)) return no('base_fare', `base fare under $${FAYE_MIN_BASE_FARE}`);
  if (!returnDate) return no('one_way', 'Faye prices a trip from departure to return, and one-way trips have no return date');
  if (fayeTripNights(departDate, returnDate) == null) return no('dates', 'departure and return dates are missing or out of order');
  const leadDays = Math.round((toUtcDay(departDate) - toUtcDay(today || new Date().toISOString())) / DAY_MS);
  if (!(leadDays >= FAYE_RULES.fullCoverageMinDaysBeforeDeparture)) {
    return no('departs_soon', `departs in under ${FAYE_RULES.fullCoverageMinDaysBeforeDeparture} days, inside Faye's covered-reason window`);
  }
  if (!(Number(remainingPayments) >= 1)) return no('no_remaining_payments', 'the premium is spread over the payments after the deposit, and there are none');
  // A missing age must not read as 0 (Number(null) === 0).
  const list = Array.isArray(ages) ? ages.map((a) => (a == null || a === '' ? NaN : Number(a))) : [];
  if (!list.length || list.some((a) => !Number.isFinite(a) || a < 0)) return no('travelers', 'every traveler needs a date of birth');
  if (region !== 'domestic' && region !== 'international') return no('region', "can't tell whether the trip leaves the US");
  const cost = Number(tripCost);
  if (!Number.isFinite(cost) || cost <= 0) return no('trip_cost', 'no trip cost to insure');
  if (cost > FAYE_RULES.maxTripCostPerTrip || cost / list.length > FAYE_RULES.maxTripCostPerTraveler) {
    return no('trip_cost_limit', 'over $100,000 per traveler or $300,000 per trip');
  }
  return { eligible: true, reason: null, detail: null };
}

/**
 * Price one Faye plan.
 *
 * @param {object}   p
 * @param {'first'|'forward'|'flagship'} p.tier
 * @param {number[]} p.ages        each traveler's age in completed years
 * @param {number}   p.tripCost    dollars the party is insuring (0 = no cancellation cover)
 * @param {string}   p.departDate  YYYY-MM-DD
 * @param {string}   p.returnDate  YYYY-MM-DD — Faye needs one even for one-way trips
 * @param {string}   [p.state]     two-letter residence state (CA is the quoted baseline)
 * @param {boolean}  [p.cfar]      add Cancel For Any Reason
 * @param {string}   [p.purchaseDate] YYYY-MM-DD the policy will be bought (default today)
 * @param {boolean}  [p.international] trip leaves the US (Faye First → Faye First International)
 * @returns {{ ok: true, tier: string, label: string, international: boolean, premium: number,
 *             insuredCost: number, nights: number,
 *             travelers: number, breakdown: object, exact: boolean, notes: string[],
 *             warnings: string[] }
 *         | { ok: false, error: string }}
 */
export function quoteFaye({ tier, ages, tripCost, departDate, returnDate, state = 'CA', cfar = false, purchaseDate = null, international = false }) {
  const t = FAYE_TIERS[tier];
  if (!t) return { ok: false, error: `Unknown Faye tier "${tier}"` };
  const travelerAges = Array.isArray(ages) ? ages.map((a) => (a == null || a === '' ? NaN : Number(a))) : [];
  const n = travelerAges.length;
  if (n === 0) return { ok: false, error: 'At least one traveler is required' };
  if (travelerAges.some((a) => !Number.isFinite(a) || a < 0)) return { ok: false, error: 'Every traveler needs an age' };
  const nights = fayeTripNights(departDate, returnDate);
  if (nights == null) return { ok: false, error: 'Faye needs both a departure and a return date' };
  const cost = Number(tripCost);
  if (!Number.isFinite(cost) || cost < 0) return { ok: false, error: 'Trip cost must be zero or more' };
  if (cost > FAYE_RULES.maxTripCostPerTrip || cost / n > FAYE_RULES.maxTripCostPerTraveler) {
    return { ok: false, error: 'Faye insures at most $100,000 per traveler and $300,000 per trip' };
  }

  const notes = [];
  // Faye insures $1,000+ (or $0 for medical/delay/baggage only).
  const insured = cost === 0 ? 0 : Math.max(FAYE_MIN_TRIP_COST, cost);
  if (cost > 0 && cost < FAYE_MIN_TRIP_COST) {
    notes.push(`priced at Faye's $1,000 minimum (trip costs $${cost.toFixed(2)})`);
  }
  let exact = true;
  const inexact = (why) => { exact = false; notes.push(why); };
  if (insured === 0) inexact('a $0 trip cost (no cancellation cover) was not quoted');
  if (insured > 10000) inexact('trip costs over $10,000 were not quoted');

  const share = insured / n;
  const bands = travelerAges.map(ageBand);
  const childCount = bands.filter((b) => b.key === 'child').length;
  const withAdult = childCount < n; // at least one traveler 18+
  const chargeable = Math.max(0, nights - 13);

  // Each traveler priced and rounded to the cent, then added up (Faye's way).
  let baseCents = 0;
  let ageCents = 0;
  let travelerCents = 0;
  for (const band of bands) {
    let base;
    let trip;
    let age = 0;
    if (band.key === 'child' && withAdult && t.familyChild) {
      base = t.familyChild.base;
      trip = t.familyChild.rate * share / 1000;
    } else {
      base = t.base;
      trip = t.slope * share / 1000;
      age = (t.ageFees?.[band.key] ?? 0) + t.ageRates[band.key] * share / 1000;
    }
    baseCents += cents(base);
    ageCents += cents(age);
    travelerCents += cents(base + trip + age);
    if (!band.tested && band.note && !notes.includes(band.note)) inexact(band.note);
  }

  const seniors = bands.filter((b) => b.key.startsWith('senior'));
  if (childCount) {
    if (!t.familyChild) inexact(`${t.label} pricing for children was not quoted`);
    else if (!withAdult && n > 1) inexact('parties of only children were not quoted');
    else if (!withAdult && share !== 2000) inexact('a child traveling alone was only quoted on a $2,000 trip');
    else if (withAdult && chargeable > 0) inexact("children's nightly rate past night 13 is provisional (one family quote)");
    if (seniors.length) inexact('parties with seniors and children were not quoted');
  }
  if (seniors.length) {
    if (share > 5000) inexact('senior pricing was quoted up to $5,000 per traveler');
    // The one long senior quote (#24: 70, $10,000, 29 nights) ran 4–11% over
    // the adult nightly rate: a senior's own rate isn't known.
    if (chargeable > 0) inexact("seniors' nightly rate past night 13 was not quoted (it runs higher than adults')");
    const quotedAt = t.seniorShareQuoted || {};
    if (seniors.some((b) => quotedAt[b.key] != null && quotedAt[b.key] !== share)) {
      inexact(`${t.label} senior pricing was only quoted on a $2,000 share`);
    }
  }
  if (n >= 3 && seniors.length) inexact('parties of 3+ with seniors were not quoted');

  if (nights > 24) inexact('trips over 24 nights were not quoted exactly (30 nights ran ~1% high)');

  let adjustCents = 0;
  if (t.tenThousandStep && insured >= 10000) {
    adjustCents += cents(t.tenThousandStep);
    inexact(`${t.label} steps up at $10,000 of trip cost (one quote)`);
  }
  const st = String(state || 'CA').toUpperCase();
  if (t.stateAdjustment?.[st]) {
    adjustCents += cents(t.stateAdjustment[st]);
    inexact(`${t.label} costs more in ${st} (one quote)`);
  } else if (!(t.quotedStates || ['CA']).includes(st)) {
    inexact(`only ${(t.quotedStates || ['CA']).join(', ')} residents were quoted for ${t.label} (rates are filed by state)`);
  }

  const tripCents = travelerCents - baseCents - ageCents;
  // Each traveler's nights past 13; a child with an adult at their own rate.
  const durationCents = bands.reduce((sum, band) => sum + chargeable * cents(
    band.key === 'child' && withAdult && t.familyChild ? t.familyChild.perNight ?? t.perNight : t.perNight,
  ), 0);
  const cfarCents = cfar ? cents(FAYE_CFAR_RATE * insured) : 0;
  const total = travelerCents + durationCents + adjustCents + cfarCents;

  // Coverage timing, separate from price exactness.
  const warnings = [];
  const bought = toUtcDay(purchaseDate || new Date().toISOString());
  const leadDays = Math.round((toUtcDay(departDate) - bought) / DAY_MS);
  if (leadDays < FAYE_RULES.fullCoverageMinDaysBeforeDeparture) {
    warnings.push(`departs ${leadDays <= 0 ? 'today or earlier' : `in ${leadDays} day${leadDays === 1 ? '' : 's'}`}: Faye's covered reasons assume buying 3+ days before departure`);
  }
  if (cfar && leadDays * 24 < FAYE_RULES.cfarMinHoursBeforeDeparture) {
    warnings.push('Cancel For Any Reason only pays when the trip is cancelled 48+ hours before departure');
  }

  return {
    ok: true,
    tier: t.key,
    label: fayePlan(t.key, { international }).label,
    international: Boolean(international),
    premium: total / 100,
    insuredCost: insured,
    nights,
    travelers: n,
    breakdown: {
      base: baseCents / 100,
      tripCost: tripCents / 100,
      age: ageCents / 100,
      duration: durationCents / 100,
      adjustments: adjustCents / 100,
      cfar: cfarCents / 100,
    },
    exact,
    notes,
    warnings,
  };
}
